import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { MenuItem, NumberSpinner } from '@/ui/primitives';
import { Select } from '@/ui/primitives/Select';
import { TextField } from '@/ui/primitives/TextField';
import { isSingularValue, toPluralUnitLabel, toSingularUnitLabel } from './utils';
import type { ReactElement } from 'react';

type ThresholdValueUnitRowProps<TUnit extends string> = {
  label: string;
  value: string;
  unit: TUnit;
  templateColumns: string;
  options: Array<{ value: TUnit; label: string }>;
  error: boolean;
  betweenText?: string;
  contextAtEnd?: boolean;
  pluralizeUnitLabelsByValue?: boolean;
  disabled?: boolean;
  useNumberField?: boolean;
  onValueChange: (next: string) => void;
  onUnitChange: (next: TUnit) => void;
};

const selectSx = {
  width: '100%',
  '&.MuiInputBase-root': {
    backgroundColor: 'background.paper',
  },
  '&.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiSelect-select.Mui-disabled': {
    WebkitTextFillColor: 'text.disabled',
    color: 'text.disabled',
  },
  '& .MuiSelect-icon.Mui-disabled': {
    color: 'text.disabled',
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '&.Mui-disabled.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
} as const;

export const ThresholdValueUnitRow = <TUnit extends string>({
  label,
  value,
  unit,
  templateColumns,
  options,
  error,
  betweenText,
  contextAtEnd = false,
  pluralizeUnitLabelsByValue = false,
  disabled = false,
  useNumberField = false,
  onValueChange,
  onUnitChange,
}: ThresholdValueUnitRowProps<TUnit>): ReactElement => {
  const selectElement = (
    <Select
      label={undefined}
      fullWidth
      value={unit}
      disabled={disabled}
      sx={selectSx}
      onChange={(event) => {
        onUnitChange(event.target.value as TUnit);
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {pluralizeUnitLabelsByValue && !isSingularValue(value)
            ? toPluralUnitLabel(option.label)
            : toSingularUnitLabel(option.label)}
        </MenuItem>
      ))}
    </Select>
  );

  const contextElement = betweenText ? (
    <Text
      role="body"
      color="textSecondary"
      sx={{ whiteSpace: 'nowrap', textAlign: 'center', justifySelf: 'center' }}
    >
      {betweenText}
    </Text>
  ) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: templateColumns,
        alignItems: 'center',
        columnGap: s('lg'),
        width: '100%',
        '& > *': { minWidth: 0 },
      }}
    >
      <Text
        role="body"
        color="textSecondary"
        sx={{ whiteSpace: 'nowrap', textAlign: 'right', justifySelf: 'end' }}
      >
        {label}
      </Text>
      {useNumberField ? (
        <NumberSpinner
          value={value}
          min={1}
          step={1}
          size="small"
          error={error}
          disabled={disabled}
          placeholder="Enter value"
          sx={[{ width: '100%' }, disabled ? { '& input': { color: 'text.disabled' } } : {}]}
          onChange={onValueChange}
        />
      ) : (
        <TextField
          label={undefined}
          placeholder="Enter value"
          type="number"
          value={value}
          error={error}
          disabled={disabled}
          sx={[
            { width: '100%' },
            disabled
              ? {
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.disabled',
                    color: 'text.disabled',
                  },
                }
              : {},
          ]}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
        />
      )}
      {contextAtEnd ? (
        <>
          {selectElement}
          {contextElement}
        </>
      ) : (
        <>
          {contextElement}
          {selectElement}
        </>
      )}
    </Box>
  );
};
